import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Pencil, Mail, Phone, Copy, ChevronRight, Lock, Sparkles, Info, TrendingDown, Store, GitMerge, MapPin, Wand2, Search, Loader2, Target, FileText } from "lucide-react";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import DuplicateReviewDialog from "../components/DuplicateReviewDialog";
import { eur, pct } from "../lib/format";

/**
 * Fournisseurs & Négociation
 * - Essentiel  → dossier verrouillé (upsell)
 * - Pro        → dossier standard (arguments génériques)
 * - Intégral   → dossier personnalisé (levier fournisseur + benchmark inter-fournisseurs)
 */
export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [recs, setRecs] = useState([]);
  const [plan, setPlan] = useState(null); // { effective_plan, limits, ... }
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", legal_name: "", vat_number: "", siren: "", siret: "", address: "", website: "" });
  const [editId, setEditId] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [dossierLoading, setDossierLoading] = useState(false);
  // Feb 2026 — Dossier fournisseur : Top 5 hausses (nouveau flow)
  const [supplierDossier, setSupplierDossier] = useState(null);
  const [supplierDossierLoading, setSupplierDossierLoading] = useState(false);
  const [negoSummaries, setNegoSummaries] = useState({}); // { [supplier_id]: {count, total_unit_saving_ht} }
  // Sous-outils dédiés : fusion, alias, sites
  const [mergeSource, setMergeSource] = useState(null);       // supplier row à fusionner
  const [mergeTarget, setMergeTarget] = useState("");         // id cible
  const [mergeSite, setMergeSite] = useState("");
  const [aliasFor, setAliasFor] = useState(null);              // supplier row
  const [aliases, setAliases] = useState([]);
  const [aliasDraft, setAliasDraft] = useState({ raw_text: "", site_name: "" });
  const [sitesFor, setSitesFor] = useState(null);              // supplier row
  const [siteDraft, setSiteDraft] = useState({ name: "", city: "", address: "", siret: "" });
  // Lookup INSEE / recherche-entreprises.api.gouv.fr — sans clé
  const [insee, setInsee] = useState({ open: false, loading: false, candidates: [] });
  const navigate = useNavigate();

  const load = async () => {
    const [s, p, r, pl, ns] = await Promise.all([
      api.suppliers(), api.products(), api.recommendations(), api.planMe().catch(() => null),
      api.negotiationSuppliersSummary().catch(() => ({ summaries: {}, locked: true })),
    ]);
    setSuppliers(s); setProducts(p); setRecs(r); setPlan(pl);
    setNegoSummaries(ns?.summaries || {});
  };
  useEffect(() => { load(); }, []);

  const negotiationLevel = plan?.limits?.negotiation || "standard"; // none | standard | personalised
  const isLocked = negotiationLevel === "none";
  const isPersonalised = negotiationLevel === "personalised";

  const save = async () => {
    if (!form.name) { toast.error("Nom requis"); return; }
    if (editId) await api.updateSupplier(editId, form); else await api.createSupplier(form);
    toast.success("Fournisseur enregistré"); setOpen(false); await load();
  };

  const openDossier = async (productId) => {
    if (isLocked) {
      toast.error("Passez en Pro pour ouvrir un dossier de négociation.");
      navigate("/parametres");
      return;
    }
    setDossierLoading(true);
    try {
      const d = await api.negotiation(productId);
      setDossier(d);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "negotiation_locked") {
        toast.error(detail.message);
        navigate("/parametres");
      } else {
        toast.error("Impossible d'ouvrir le dossier.");
      }
    } finally { setDossierLoading(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copié dans le presse-papier"); };

  // Feb 2026 — Ouvre le dossier de négociation par FOURNISSEUR (Top 5 hausses).
  const openSupplierDossier = async (supplierId) => {
    if (isLocked) {
      toast.error("Passez en Pro pour ouvrir un dossier de négociation.");
      navigate("/parametres");
      return;
    }
    setSupplierDossierLoading(true);
    try {
      const d = await api.negotiationSupplier(supplierId);
      setSupplierDossier(d);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "negotiation_locked") {
        toast.error(detail.message);
        navigate("/parametres");
      } else {
        toast.error("Impossible d'ouvrir le dossier fournisseur.");
      }
    } finally { setSupplierDossierLoading(false); }
  };

  // Détection de doublons — flow sûr avec revue manuelle par paire
  const [reviewPairs, setReviewPairs] = useState(null);
  const runCleanup = async () => {
    try {
      const res = await api.cleanupSuppliers();
      const merged = res.auto_merges?.length || 0;
      if (merged) {
        toast.success(`${merged} fournisseur${merged > 1 ? "s" : ""} regroupé${merged > 1 ? "s" : ""}`);
        await load();
      }
      if (!res.pairs || res.pairs.length === 0) { if (!merged) toast.info("Aucun doublon détecté"); return; }
      setReviewPairs({ open: true, pairs: res.pairs });
    } catch { toast.error("Impossible d'analyser les doublons"); }
  };

  const doMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    if (!window.confirm(`Fusionner « ${mergeSource.name} » DANS « ${suppliers.find((x) => x.id === mergeTarget)?.name} » ? Factures, produits, alias et historique seront conservés.`)) return;
    try {
      await api.mergeSuppliers(mergeSource.id, mergeTarget, mergeSite || null);
      toast.success("Fusion effectuée");
      setMergeSource(null); setMergeTarget(""); setMergeSite("");
      await load();
    } catch { toast.error("Échec de la fusion"); }
  };

  const openAliasDialog = async (s) => {
    setAliasFor(s); setAliasDraft({ raw_text: "", site_name: "" });
    try { setAliases(await api.supplierAliases(s.id)); } catch { setAliases([]); }
  };
  const addAlias = async () => {
    if (!aliasDraft.raw_text.trim()) { toast.error("Écriture requise"); return; }
    try {
      await api.addSupplierAlias({ raw_text: aliasDraft.raw_text.trim(), supplier_id: aliasFor.id, site_name: aliasDraft.site_name || null, confidence: 1.0 });
      setAliasDraft({ raw_text: "", site_name: "" });
      setAliases(await api.supplierAliases(aliasFor.id));
      toast.success("Alias ajouté");
    } catch { toast.error("Échec d'ajout"); }
  };
  const removeAlias = async (rk) => {
    try { await api.deleteSupplierAlias(rk); setAliases(await api.supplierAliases(aliasFor.id)); }
    catch { toast.error("Échec suppression"); }
  };

  const addSite = async () => {
    if (!siteDraft.name.trim()) { toast.error("Nom du site requis"); return; }
    try {
      await api.addSupplierSite(sitesFor.id, { ...siteDraft, name: siteDraft.name.trim() });
      setSiteDraft({ name: "", city: "", address: "", siret: "" });
      const fresh = (await api.suppliers()).find((s) => s.id === sitesFor.id);
      setSitesFor(fresh || sitesFor);
      await load();
      toast.success("Site ajouté");
    } catch { toast.error("Échec d'ajout du site"); }
  };
  const removeSite = async (siteId) => {
    try {
      await api.deleteSupplierSite(sitesFor.id, siteId);
      const fresh = (await api.suppliers()).find((s) => s.id === sitesFor.id);
      setSitesFor(fresh || sitesFor);
      await load();
    } catch { toast.error("Échec suppression site"); }
  };

  // Auto-remplissage depuis l'API INSEE / Sirene (source publique gratuite,
  // aucune clé requise). Interroge à la volée dès qu'on tape « Rechercher »
  // dans le formulaire fournisseur.
  const runInseeLookup = async () => {
    const q = (form.name || form.legal_name || "").trim();
    if (q.length < 2) { toast.error("Saisis au moins 2 caractères dans « Nom »"); return; }
    setInsee({ open: true, loading: true, candidates: [] });
    try {
      const res = await api.supplierLegalLookup(q);
      setInsee({ open: true, loading: false, candidates: res.candidates || [] });
      if ((res.candidates || []).length === 0) toast.info("Aucune entreprise trouvée");
    } catch {
      setInsee({ open: true, loading: false, candidates: [] });
      toast.error("API Sirene indisponible");
    }
  };
  const applyInseeCandidate = (c) => {
    setForm((f) => ({
      ...f,
      legal_name: c.legal_name || f.legal_name,
      name: f.name || c.commercial_name || c.legal_name || "",
      siren: c.siren || f.siren,
      siret: c.siret || f.siret,
      vat_number: c.vat_number || f.vat_number,
      address: [c.address, c.city].filter(Boolean).join(" · ") || f.address,
    }));
    setInsee({ open: false, loading: false, candidates: [] });
    toast.success("Informations légales pré-remplies");
  };

  return (
    <div className="space-y-5">
      <PageHero
        eyebrow="Négociation"
        title="Fournisseurs & Négociation"
        subtitle={`${suppliers.length} fournisseur(s) · dossiers de négociation prêts à l'emploi`}
        pattern="grid"
        kpi={recs.length && !isLocked ? { label: "Dossiers", value: recs.length, hint: "à ouvrir" } : null}
        action={
          <div className="flex items-center gap-2">
            <button data-testid="cleanup-suppliers-btn" onClick={runCleanup} className="h-11 px-3 rounded-lg border border-[#CDD7E3] bg-white text-[13px] font-semibold text-[#0B1F33] inline-flex items-center gap-1.5 hover:bg-[#F4F5EF]" title="Fusionner automatiquement les doublons évidents">
              <Wand2 className="w-4 h-4" /> Nettoyer les doublons
            </button>
            <button data-testid="new-supplier-btn" onClick={() => { setForm({ name: "", email: "", phone: "", notes: "", legal_name: "", vat_number: "", siren: "", siret: "", address: "", website: "" }); setEditId(null); setOpen(true); }} className="btn-orange !h-11">
              <Plus className="w-4 h-4" /> Nouveau fournisseur
            </button>
          </div>
        }
      />

      {/* Verrou Essentiel */}
      {isLocked && (
        <div
          data-testid="negotiation-locked-banner"
          className="surface p-5 border-l-[3px] border-l-[#FF9F1C] flex flex-wrap gap-4 items-start"
        >
          <div className="w-10 h-10 rounded-lg bg-[#FFF0D2] flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5 text-[#7A4A00]" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[15px] font-heading font-extrabold text-[#0B1F33]">
              Débloquez l'aide à la négociation
            </div>
            <p className="mt-1 text-[13px] text-[#5E6875] leading-relaxed">
              Votre formule Essentiel n'inclut pas les dossiers de négociation. Passez en{" "}
              <b className="text-[#0B1F33]">Pro</b> pour recevoir des argumentaires standards, ou en{" "}
              <b className="text-[#0B1F33]">Intégral</b> pour un dossier personnalisé par fournisseur (levier volume, benchmark, e-mail sur-mesure).
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <button
                type="button"
                data-testid="negotiation-upgrade-btn"
                onClick={() => navigate("/parametres")}
                className="h-10 px-5 rounded-lg bg-[#FF9F1C] hover:bg-[#F58F06] text-[#0B1F33] text-[13px] font-extrabold inline-flex items-center gap-1.5 transition-colors"
              >
                Voir les formules <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLocked && recs.length > 0 && (
        <div className="surface p-4 border-l-[3px] border-l-[#FF9F1C]">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-md bg-[#FFF0D2] flex items-center justify-center">
              <Mail className="w-3.5 h-3.5 text-[#7A4A00]" />
            </div>
            <h3 className="section-title text-[15px]">
              Dossiers de négociation suggérés
              {isPersonalised && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#0B1F33] text-white text-[10px] font-bold uppercase tracking-widest">
                  <Sparkles className="w-3 h-3 text-[#FF9F1C]" /> Personnalisé
                </span>
              )}
            </h3>
            <span className="pill pill-warn ml-1">{recs.length}</span>
          </div>
          <div className="space-y-2">
            {recs.map((r) => (
              <div key={r.id} className="flex items-center justify-between border border-[#CDD7E3] rounded-md p-3 flex-wrap gap-2 bg-white">
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => navigate(`/produits?open=${r.product_id}`)}
                    data-testid={`open-product-${r.product_id}`}
                    className="text-left text-[14px] font-semibold text-[#0B1F33] hover:text-[#1769E0] hover:underline decoration-2 underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9F1C] rounded-sm inline-flex items-center gap-1 flex-wrap"
                  >
                    <span className="break-words">{r.product_name}</span>
                    <ChevronRight className="w-3.5 h-3.5 opacity-60 shrink-0" aria-hidden />
                    <span className="text-[#5E6875] font-normal text-[13px] break-words">chez {r.supplier_name}</span>
                  </button>
                  <div
                    className="text-[12.5px] text-[#0B1F33] mt-1 font-mono-num font-semibold"
                    data-testid={`nego-card-stats-${r.product_id}`}
                  >
                    {(() => {
                      // Feb 2026 §« 3 chiffres maximum sur la carte » —
                      // Ancien prix HT, nouveau prix HT, économie possible
                      // par unité (SI retour au précédent tarif). Aucun
                      // gain annuel. Aucun gain par commande. Aucune fausse
                      // urgence. « Non calculable » si les données manquent.
                      const u = r.base_unit === "u" ? "pièce" : (r.base_unit || "");
                      const before = Number(r.price_before_ht ?? NaN);
                      const now = Number(r.price_current_ht ?? NaN);
                      const saving = Number(r.unit_saving_ht ?? NaN);
                      if (!Number.isFinite(before) || !Number.isFinite(now) || !u) {
                        return <>Hausse <span className="text-[#D84343]">+{pct(r.variation_pct)}</span> · <span className="text-[#5E6875]">économie non calculable</span></>;
                      }
                      const beforeStr = before.toFixed(2).replace(".", ",");
                      const nowStr = now.toFixed(2).replace(".", ",");
                      const savingStr = Number.isFinite(saving) && saving > 0
                        ? saving.toFixed(2).replace(".", ",")
                        : "0,00";
                      return <>
                        <span className="text-[#5E6875] font-normal">Ancien</span>{" "}<span>{beforeStr} €/{u}</span>
                        {" → "}
                        <span className="text-[#5E6875] font-normal">Nouveau</span>{" "}<span>{nowStr} €/{u}</span>
                        {saving > 0 && (
                          <>
                            {" · "}
                            <span className="text-[#24845B]">économie possible {savingStr} €/{u}</span>
                            <span className="text-[#5E6875] font-normal text-[11.5px]"> si retour au précédent tarif</span>
                          </>
                        )}
                      </>;
                    })()}
                  </div>
                </div>
                <button
                  data-testid={`open-negotiation-${r.product_id}`}
                  onClick={() => openDossier(r.product_id)}
                  disabled={dossierLoading}
                  className="shrink-0 w-full sm:w-auto h-10 px-4 sm:px-5 rounded-lg bg-[#FF9F1C] hover:bg-[#F58F06] text-[#0B1F33] text-[13px] font-extrabold inline-flex items-center justify-center gap-1.5 transition-colors shadow-[0_1px_0_rgba(217,122,18,0.35)] disabled:opacity-60"
                  style={{ backgroundColor: "#FF9F1C", color: "#0B1F33" }}
                >
                  Ouvrir le dossier <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="surface overflow-x-auto">
        <table className="w-full dense min-w-[320px]">
          <thead>
            <tr>
              <th>Nom</th>
              <th className="hidden md:table-cell">Email</th>
              <th className="hidden md:table-cell">Téléphone</th>
              <th className="num hidden sm:table-cell">Produits</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 && (
              <tr><td colSpan={5} className="text-center text-slate-500 py-8">Aucun fournisseur.</td></tr>
            )}
            {suppliers.map((s) => {
              const count = products.filter((p) => p.supplier_id === s.id).length;
              // Feb 2026 §« stats dans dossiers » — On CONSERVE le chargement
              // de `negoSummaries` (payé une fois côté backend, alimente les
              // cartes de suggestion + dossier ouvert) mais on n'en extrait
              // plus de badge sur la ligne fournisseur : plus de decoration
              // gain / hausse ici.
              return (
                <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <td className="font-medium">
                    <div className="text-[#0B1F33] break-words">{s.name}</div>
                    {(s.legal_name || s.siren || s.vat_number) && (
                      <div className="text-[10.5px] text-[#5E6875] mt-0.5 font-mono-num break-words">
                        {s.legal_name ? `${s.legal_name} · ` : ""}{s.vat_number ? `TVA ${s.vat_number}` : ""}{s.siren ? ` · SIREN ${s.siren}` : ""}
                      </div>
                    )}
                    {/* Feb 2026 — infos condensées visibles sur mobile
                        (colonnes email/tel/produits masquées < md pour libérer
                        la place aux boutons d'action). */}
                    <div className="md:hidden text-[11.5px] text-[#5E6875] mt-1 space-y-0.5">
                      {s.email && <div className="truncate">{s.email}</div>}
                      {s.phone && <div className="font-mono-num">{s.phone}</div>}
                      <div className="font-mono-num sm:hidden">{count} produit{count > 1 ? "s" : ""}</div>
                    </div>
                  </td>
                  <td className="text-secondary hidden md:table-cell">{s.email || "—"}</td>
                  <td className="text-secondary hidden md:table-cell">{s.phone || "—"}</td>
                  <td className="num hidden sm:table-cell">{count}</td>
                  <td className="text-right whitespace-nowrap px-2 sm:px-3.5">
                    {/* Feb 2026 §« stats dans dossiers, pas sur la ligne » —
                        Ligne fournisseur = identité + coordonnées + nb produits
                        + actions. Le badge « X hausses / Y € potentiels » a
                        migré vers les cartes de la section « Dossiers de
                        négociation suggérés » et dans le dossier ouvert.
                        `negoSummaries` reste chargé (payé une fois) pour
                        garder les cartes cohérentes — pas de recalcul. */}
                    <div className="inline-flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
                      <Button size="sm" variant="ghost" className="!h-9 !w-9 !p-0" title="Fusionner avec un autre fournisseur" data-testid={`supplier-merge-btn-${s.id}`} onClick={() => { setMergeSource(s); setMergeTarget(""); setMergeSite(""); }}><GitMerge className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="!h-9 !w-9 !p-0" title="Modifier le fournisseur" data-testid={`supplier-edit-btn-${s.id}`} onClick={() => { setForm({ name: s.name, email: s.email || "", phone: s.phone || "", notes: s.notes || "", legal_name: s.legal_name || "", vat_number: s.vat_number || "", siren: s.siren || "", siret: s.siret || "", address: s.address || "", website: s.website || "" }); setEditId(s.id); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="!h-9 !w-9 !p-0" title="Supprimer" data-testid={`supplier-delete-btn-${s.id}`} onClick={async () => { if (window.confirm(`Supprimer « ${s.name} » ?`)) { await api.deleteSupplier(s.id); load(); } }}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouveau"} fournisseur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom</Label>
              <div className="flex gap-2">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="flex-1" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={runInseeLookup}
                  data-testid="supplier-insee-lookup"
                  title="Rechercher SIREN/SIRET/adresse via l'annuaire officiel"
                  className="whitespace-nowrap gap-1"
                >
                  <Search className="w-3.5 h-3.5" /> Rechercher INSEE
                </Button>
              </div>
              <div className="text-[10.5px] text-[#5E6875] mt-1">
                Source officielle Etalab · <a href="https://recherche-entreprises.api.gouv.fr" target="_blank" rel="noreferrer" className="underline">recherche-entreprises</a> (aucune clé, données INSEE / Sirene)
              </div>
            </div>
            <div><Label>Raison sociale</Label><Input value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>TVA intracom.</Label><Input value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="FR12345678901" /></div>
              <div><Label>SIREN</Label><Input value={form.siren || ""} onChange={(e) => setForm({ ...form, siren: e.target.value })} placeholder="9 chiffres" /></div>
              <div><Label>SIRET</Label><Input value={form.siret || ""} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="14 chiffres" /></div>
              <div><Label>Site web</Label><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="metro.fr" /></div>
            </div>
            <div><Label>Adresse</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-[#2878FF] hover:bg-[#1769E0] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Résultats INSEE / Sirene — sélection d'un candidat */}
      <Dialog open={insee.open} onOpenChange={(v) => setInsee((s) => ({ ...s, open: v }))}>
        <DialogContent className="max-w-2xl" data-testid="supplier-insee-dialog">
          <DialogHeader>
            <DialogTitle>Résultats INSEE / Sirene</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-[13px]">
            <p className="text-[11.5px] text-[#5E6875]">Sélectionne l'entreprise correspondant à ce fournisseur — SIREN, SIRET, TVA et adresse seront pré-remplis dans le formulaire. Aucune donnée n'est envoyée à un tiers en dehors de l'API publique du gouvernement.</p>
            {insee.loading ? (
              <div className="py-8 flex items-center justify-center gap-2 text-[#5E6875]">
                <Loader2 className="w-4 h-4 animate-spin" /> Recherche en cours…
              </div>
            ) : insee.candidates.length === 0 ? (
              <div className="py-6 text-center text-[12px] text-[#5E6875] italic">Aucun résultat — précise le nom (raison sociale) ou ajoute une ville.</div>
            ) : (
              <ul className="divide-y divide-[#CDD7E3] rounded-md border border-[#CDD7E3] max-h-[60vh] overflow-y-auto">
                {insee.candidates.map((c) => (
                  <li key={c.siren || c.siret || c.legal_name} data-testid={`insee-candidate-${c.siren || c.siret}`}>
                    <button
                      type="button"
                      onClick={() => applyInseeCandidate(c)}
                      className="w-full text-left px-3 py-2.5 hover:bg-[#F4F5EF] focus-visible:outline-none focus-visible:bg-[#F4F5EF] transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[13.5px] font-semibold text-[#0B1F33] truncate">{c.legal_name || c.commercial_name}</div>
                          <div className="text-[11.5px] text-[#5E6875] font-mono-num mt-0.5">
                            SIREN {c.siren || "—"} · SIRET {c.siret || "—"} · TVA {c.vat_number || "—"}
                          </div>
                          <div className="text-[11.5px] text-[#5E6875] mt-0.5 truncate">
                            <MapPin className="w-3 h-3 inline-block -mt-0.5 mr-0.5" />{c.address || "—"}{c.city ? ` · ${c.city}` : ""}
                          </div>
                          {c.activity && <div className="text-[10.5px] text-[#8A94A6] mt-0.5">Activité NAF {c.activity}</div>}
                          {c.nb_etablissements ? <div className="text-[10.5px] text-[#8A94A6]">{c.nb_etablissements} établissement(s)</div> : null}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#5E6875] shrink-0" />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fusion fournisseur */}
      <Dialog open={!!mergeSource} onOpenChange={(v) => !v && setMergeSource(null)}>
        <DialogContent className="max-w-lg" data-testid="supplier-merge-dialog">
          <DialogHeader><DialogTitle>Fusionner « {mergeSource?.name} »</DialogTitle></DialogHeader>
          <div className="space-y-3 text-[13px] text-[#0B1F33]">
            <p className="text-[12px] text-[#5E6875]">Toutes les factures, produits, historiques et alias resteront intacts et seront rattachés au fournisseur cible. Un site sera créé pour tracer l'origine.</p>
            <div>
              <Label>Fournisseur cible</Label>
              <select
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
                data-testid="supplier-merge-target"
                className="h-10 rounded-md border border-[#CDD7E3] bg-white w-full px-2 text-[13px]"
              >
                <option value="">— Sélectionner —</option>
                {suppliers.filter((x) => x.id !== mergeSource?.id).map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Nom du site à créer (facultatif)</Label>
              <Input value={mergeSite} onChange={(e) => setMergeSite(e.target.value)} placeholder="Ex : Marseille Les Arnavaux" data-testid="supplier-merge-site" />
              <div className="text-[11px] text-[#5E6875] mt-1">Laisser vide pour utiliser automatiquement le nom source.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeSource(null)}>Annuler</Button>
            <Button onClick={doMerge} className="bg-[#FF9F1C] hover:bg-[#F58F06] text-[#0B1F33] font-bold" data-testid="supplier-merge-confirm">
              <GitMerge className="w-4 h-4 mr-1" /> Fusionner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alias fournisseur */}
      <Dialog open={!!aliasFor} onOpenChange={(v) => !v && setAliasFor(null)}>
        <DialogContent className="max-w-lg" data-testid="supplier-alias-dialog">
          <DialogHeader><DialogTitle>Alias de « {aliasFor?.name} »</DialogTitle></DialogHeader>
          <div className="space-y-2 text-[13px]">
            <p className="text-[12px] text-[#5E6875]">Chaque écriture ci-dessous a été validée : les prochaines factures qui la contiennent seront automatiquement rattachées à ce fournisseur.</p>
            {aliases.length === 0 ? (
              <div className="text-[12px] text-[#5E6875] italic py-2">Aucun alias enregistré.</div>
            ) : (
              <ul className="divide-y divide-[#CDD7E3] rounded-md border border-[#CDD7E3]">
                {aliases.map((a) => (
                  <li key={a.raw_key} className="px-3 py-2 flex items-center gap-2" data-testid={`supplier-alias-row-${a.raw_key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-num font-bold text-[12.5px] text-[#0B1F33] truncate">« {a.raw_text || a.raw_key} »</div>
                      <div className="text-[10.5px] text-[#5E6875]">{a.site_name ? `Site : ${a.site_name} · ` : ""}confiance {Math.round((a.confidence || 0) * 100)}%</div>
                    </div>
                    <button className="w-7 h-7 rounded hover:bg-[#FDECEC] text-[#D84343] flex items-center justify-center" onClick={() => removeAlias(a.raw_key)} title="Supprimer">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 pt-2 border-t border-dashed border-[#CDD7E3]">
              <Input placeholder="Texte brut facture (ex: METRO C&C Marseille)" value={aliasDraft.raw_text} onChange={(e) => setAliasDraft({ ...aliasDraft, raw_text: e.target.value })} data-testid="supplier-alias-input-raw" />
              <Input placeholder="Site (facultatif)" value={aliasDraft.site_name} onChange={(e) => setAliasDraft({ ...aliasDraft, site_name: e.target.value })} data-testid="supplier-alias-input-site" />
              <Button onClick={addAlias} data-testid="supplier-alias-add" className="bg-[#0B1F33] hover:bg-[#1a3050] text-white gap-1"><Plus className="w-3.5 h-3.5" /> Ajouter</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sites fournisseur */}
      <Dialog open={!!sitesFor} onOpenChange={(v) => !v && setSitesFor(null)}>
        <DialogContent className="max-w-lg" data-testid="supplier-sites-dialog">
          <DialogHeader><DialogTitle>Sites de « {sitesFor?.name} »</DialogTitle></DialogHeader>
          <div className="space-y-2 text-[13px]">
            {(!sitesFor?.sites || sitesFor.sites.length === 0) ? (
              <div className="text-[12px] text-[#5E6875] italic py-2">Aucun site rattaché.</div>
            ) : (
              <ul className="divide-y divide-[#CDD7E3] rounded-md border border-[#CDD7E3]">
                {(sitesFor?.sites || []).map((x) => (
                  <li key={x.id} className="px-3 py-2 flex items-center gap-2" data-testid={`supplier-site-row-${x.id}`}>
                    <MapPin className="w-3.5 h-3.5 text-[#1769E0]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#0B1F33]">{x.name}</div>
                      <div className="text-[10.5px] text-[#5E6875]">{[x.city, x.address, x.siret ? `SIRET ${x.siret}` : null].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                    <button className="w-7 h-7 rounded hover:bg-[#FDECEC] text-[#D84343] flex items-center justify-center" onClick={() => removeSite(x.id)} title="Supprimer">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-dashed border-[#CDD7E3]">
              <Input placeholder="Nom du site (Marseille Les Arnavaux)" value={siteDraft.name} onChange={(e) => setSiteDraft({ ...siteDraft, name: e.target.value })} data-testid="supplier-site-input-name" />
              <Input placeholder="Ville" value={siteDraft.city} onChange={(e) => setSiteDraft({ ...siteDraft, city: e.target.value })} />
              <Input placeholder="Adresse" value={siteDraft.address} onChange={(e) => setSiteDraft({ ...siteDraft, address: e.target.value })} className="col-span-2" />
              <Input placeholder="SIRET (facultatif)" value={siteDraft.siret} onChange={(e) => setSiteDraft({ ...siteDraft, siret: e.target.value })} />
              <Button onClick={addSite} data-testid="supplier-site-add" className="bg-[#0B1F33] hover:bg-[#1a3050] text-white gap-1"><Plus className="w-3.5 h-3.5" /> Ajouter le site</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dossier} onOpenChange={(v) => !v && setDossier(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="negotiation-dossier">
          {dossier && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  Dossier de négociation — {dossier.product?.name}
                </DialogTitle>
              </DialogHeader>

              {/* Feb 2026 §« 3 chiffres maximum dans le dossier produit » —
                  Ancien prix HT · Nouveau prix HT · Économie possible / unité.
                  Aucun bloc annuel, aucun prix cible séparé, aucun gain par
                  commande. Aucune répétition avec l'argumentaire. */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-testid="dossier-three-numbers">
                {(() => {
                  const gpe = dossier.gain_potentiel_estime || {};
                  const u = gpe.unit_base === "u" ? "pièce" : (gpe.unit_base || "");
                  const before = Number(gpe.target_price_ht ?? dossier.target_price_ht ?? NaN);
                  const now = Number(gpe.current_price_ht ?? dossier.current_price_ht ?? NaN);
                  const saving = Number(gpe.unit_saving_ht ?? NaN);
                  const fmt = (v) => Number.isFinite(v) ? v.toFixed(2).replace(".", ",") : "—";
                  return <>
                    <div className="bg-slate-50 p-3 rounded" data-testid="dossier-price-before">
                      <div className="text-xs text-slate-500 uppercase">Ancien prix HT</div>
                      <div className="font-mono-num font-semibold text-[#0B1F33] mt-1">
                        {fmt(before)}<span className="text-[11px] text-[#5E6875]"> €/{u}</span>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded" data-testid="dossier-price-current">
                      <div className="text-xs text-slate-500 uppercase">Nouveau prix HT</div>
                      <div className="font-mono-num font-semibold text-[#0B1F33] mt-1">
                        {fmt(now)}<span className="text-[11px] text-[#5E6875]"> €/{u}</span>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-3 rounded" data-testid="dossier-unit-saving">
                      <div className="text-xs text-slate-500 uppercase">Économie possible / unité</div>
                      {Number.isFinite(saving) && saving > 0 ? (
                        <>
                          <div className="font-mono-num font-semibold text-[#24845B] mt-1">
                            {fmt(saving)}<span className="text-[11px] text-[#24845B]"> €/{u}</span>
                          </div>
                          <div className="text-[10.5px] text-slate-500 mt-0.5">si retour au précédent tarif</div>
                        </>
                      ) : (
                        <div className="font-semibold text-[#5E6875] mt-1">Non calculable</div>
                      )}
                    </div>
                  </>;
                })()}
              </div>

              {/* Sections exclusives Intégral */}
              {dossier.plan_level === "personalised" && dossier.supplier_context && (
                <div
                  data-testid="dossier-supplier-context"
                  className="mt-2 rounded-lg border border-[#0B1F33]/15 bg-gradient-to-br from-[#FFF7E5] to-white p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Store className="w-4 h-4 text-[#0B1F33]" />
                    <h4 className="font-heading text-[14px] font-extrabold text-[#0B1F33]">Levier fournisseur — {dossier.supplier_context.supplier_name}</h4>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-[12.5px]">
                    <div className="bg-white p-2.5 rounded border border-[#0B1F33]/10">
                      <div className="text-[10.5px] uppercase tracking-widest text-[#5E6875] font-bold">Volume total / an</div>
                      <div className="mt-1 font-mono-num font-semibold text-[#0B1F33]">{eur(dossier.supplier_context.annual_spend_total)}</div>
                    </div>
                    <div className="bg-white p-2.5 rounded border border-[#0B1F33]/10">
                      <div className="text-[10.5px] uppercase tracking-widest text-[#5E6875] font-bold">Références</div>
                      <div className="mt-1 font-mono-num font-semibold text-[#0B1F33]">{dossier.supplier_context.products_count}</div>
                    </div>
                    <div className="bg-white p-2.5 rounded border border-[#0B1F33]/10">
                      <div className="text-[10.5px] uppercase tracking-widest text-[#5E6875] font-bold">Hausses détectées</div>
                      <div className={`mt-1 font-mono-num font-semibold ${dossier.supplier_context.hikes_count > 0 ? "text-[#D84343]" : "text-[#0B1F33]"}`}>
                        {dossier.supplier_context.hikes_count}
                      </div>
                    </div>
                  </div>
                  {dossier.supplier_context.hikes_details?.length > 0 && (
                    <ul className="mt-3 space-y-1 text-[12.5px] text-[#0B1F33]">
                      {dossier.supplier_context.hikes_details.map((h) => (
                        <li key={h.name} className="flex items-center justify-between border-b border-[#0B1F33]/8 py-1 last:border-0">
                          <span className="font-medium">{h.name}</span>
                          <span className="font-mono-num text-[#D84343]">+{pct(h.variation_pct)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {dossier.plan_level === "personalised" && dossier.benchmark?.length > 0 && (
                <div
                  data-testid="dossier-benchmark"
                  className="rounded-lg border border-[#24845B]/20 bg-[#F0FAF5] p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-[#24845B]" />
                    <h4 className="font-heading text-[14px] font-extrabold text-[#0B1F33]">Benchmark inter-fournisseurs</h4>
                  </div>
                  <ul className="space-y-1.5 text-[12.5px]">
                    {dossier.benchmark.map((b) => (
                      <li key={b.product_id} className="flex items-center justify-between bg-white rounded border border-[#24845B]/15 px-3 py-2">
                        <div>
                          <div className="font-semibold text-[#0B1F33]">{b.product_name}</div>
                          <div className="text-[11.5px] text-[#5E6875]">chez {b.supplier_name}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono-num font-semibold text-[#0B1F33]">{eur(b.unit_cost)} / {b.unit}</div>
                          <div className="font-mono-num text-[11.5px] font-bold text-[#24845B]">-{pct(b.savings_pct)}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Feb 2026 §« brouillon bref/naturel/modifiable » —
                  Un seul texte court, éditable en place, qui reprend
                  produit/dates/prix réels. Suppression des blocs répétitifs
                  (« Trame d'appel », « Arguments ») dans cette vue —
                  restent alimentés côté backend pour compatibilité mais
                  n'encombrent plus la fenêtre. */}
              <ProductDossierMessage dossier={dossier} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Dossier fournisseur : Top 5 hausses */}
      <Dialog open={!!supplierDossier} onOpenChange={(v) => !v && setSupplierDossier(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-negotiation-dossier">
          {supplierDossier && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <Target className="w-5 h-5 text-[#FF9F1C]" />
                  Dossier de négociation — {supplierDossier.supplier?.name}
                  <span className="text-[10.5px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[#F4F5EF] text-[#5E6875] border border-[#CDD7E3]">
                    Top {supplierDossier.count || 0} hausse{(supplierDossier.count || 0) > 1 ? "s" : ""}
                  </span>
                </DialogTitle>
              </DialogHeader>

              {/* En-tête : gain potentiel cumulé */}
              <div
                data-testid="supplier-dossier-header-total"
                className="rounded-lg border border-[#FF9F1C]/40 bg-gradient-to-br from-[#FFF7E5] to-white p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">
                    Gain potentiel cumulé
                  </div>
                  <div className="mt-1 font-mono-num text-[26px] font-extrabold text-[#0B1F33]">
                    {supplierDossier.total_saving_ht == null ? "À estimer" : eur(supplierDossier.total_saving_ht)}
                    <span className="text-[13px] font-bold text-[#5E6875] ml-1">HT potentiels</span>
                  </div>
                  <div className="text-[11.5px] text-[#5E6875] mt-1">
                    {supplierDossier.period_label || "Depuis le début de l'historique disponible"}
                  </div>
                </div>
                {supplierDossier.total_partial && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FDECEC] border border-[#D84343]/30 text-[#D84343] text-[11px] font-bold">
                    <Info className="w-3 h-3" /> Total partiel
                  </span>
                )}
              </div>

              {/* Cas vide */}
              {supplierDossier.empty_message && (
                <div
                  data-testid="supplier-dossier-empty"
                  className="rounded-lg border border-[#CDD7E3] bg-[#F4F5EF] p-5 text-center text-[13px] text-[#5E6875] italic"
                >
                  {supplierDossier.empty_message}
                </div>
              )}

              {/* Tableau des 5 produits */}
              {supplierDossier.top_hikes && supplierDossier.top_hikes.length > 0 && (
                <div className="surface overflow-x-auto" data-testid="supplier-dossier-table">
                  <table className="w-full dense min-w-[520px] text-[12.5px]">
                    <thead>
                      <tr>
                        <th className="text-left">#</th>
                        <th className="text-left">Produit</th>
                        <th className="num">Ancien prix HT</th>
                        <th className="num">Nouveau prix HT</th>
                        <th className="num">Hausse</th>
                        <th className="num">Économie possible / unité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierDossier.top_hikes.map((h, i) => {
                        // Feb 2026 §« économie possible par unité » —
                        // Affiche max(0, nouveau − ancien) sur la même
                        // base pièce/kg/L. « Non calculable » si les
                        // observations ne partagent pas la même unité
                        // normalisée (garde-fou supplier_pair en amont).
                        const unitLabel = h.base_unit === "u" ? "pièce" : h.base_unit;
                        const saving = Math.max(0, (h.unit_saving_ht ?? 0));
                        const savingCell = saving > 0
                          ? <><span className="font-mono-num font-bold text-[#24845B]">{eur(saving)}</span><span className="text-[10px] text-[#24845B]"> /{unitLabel}</span></>
                          : <span className="text-[11px] text-[#5E6875] italic">non calculable</span>;
                        return (
                          <tr key={h.product_id} data-testid={`supplier-hike-row-${h.product_id}`}>
                            <td className="text-[#5E6875] font-bold">{i + 1}</td>
                            <td className="font-semibold text-[#0B1F33] break-words">
                              {h.product_name}
                              <div className="text-[10.5px] font-normal text-[#5E6875] font-mono-num">
                                base €/{unitLabel}
                              </div>
                            </td>
                            <td className="num font-mono-num text-[#0B1F33]">{eur(h.price_before_ht)}<span className="text-[10px] text-[#5E6875]"> /{unitLabel}</span></td>
                            <td className="num font-mono-num text-[#0B1F33]">{eur(h.price_current_ht)}<span className="text-[10px] text-[#5E6875]"> /{unitLabel}</span></td>
                            <td className="num font-mono-num text-[#D84343] font-bold">+{pct(h.variation_pct)}</td>
                            <td className="num" data-testid={`supplier-hike-unit-saving-${h.product_id}`}>{savingCell}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Feb 2026 §« mise au point » — Note d'hypothèse simplifiée
                  (pas de somme €/kg + €/L, pas de gain annuel, pas
                  d'engagement). */}
              {supplierDossier.top_hikes && supplierDossier.top_hikes.length > 0 && (
                <div className="rounded-lg border border-[#CDD7E3] bg-white p-3 text-[11.5px] text-[#5E6875] leading-relaxed" data-testid="supplier-dossier-note">
                  <Info className="w-3.5 h-3.5 inline-block mr-1 text-[#1769E0]" />
                  L'économie possible par unité correspond à l'écart entre l'ancien et le nouveau prix normalisé, sur la même base. Ce n'est ni un gain annuel, ni un gain par commande, ni un engagement du fournisseur. Elle est recalculée après chaque correction de facture.
                </div>
              )}

              {/* Argumentaire */}
              {supplierDossier.top_hikes && supplierDossier.top_hikes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#0B1F33] mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Argumentaire (modifiable avant envoi)
                  </h4>
                  {/* Feb 2026 §« texte humain court et modifiable » —
                      Le message par défaut cible UNE hausse (la plus
                      importante) avec un ton neutre et concret. L'utilisateur
                      peut le modifier librement avant de copier. Aucun
                      chiffre annuel, aucun jargon, aucune fausse urgence.
                      Format identique à l'exemple validé Quentin :
                        « Bonjour, le tarif des <produit> est passé de
                          <avant> à <actuel> €/<unité> entre nos deux
                          dernières factures. Cette hausse réduit notre
                          marge. Pouvez-vous revoir ce tarif et vous
                          rapprocher du précédent prix ? Merci pour
                          votre retour. » */}
                  <SupplierNegotiationMessage dossier={supplierDossier} />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revue manuelle des fournisseurs potentiellement doublons */}
      <DuplicateReviewDialog
        open={!!reviewPairs?.open}
        kind="supplier"
        pairs={reviewPairs?.pairs || []}
        onClose={() => setReviewPairs(null)}
        onCompleted={() => load()}
      />
    </div>
  );
}

/**
 * SupplierNegotiationMessage — Feb 2026 §« texte humain court modifiable »
 *
 * Génère un brouillon d'email court et concret pour la première hausse du
 * dossier, formaté à partir des vrais prix normalisés (mêmes lignes que
 * fiche produit / historique). L'utilisateur peut ensuite le modifier
 * librement avant de copier. Aucun envoi.
 *
 * Contrat :
 *   * Utilise UNIQUEMENT `unit_saving_ht`, `price_before_ht`,
 *     `price_current_ht`, `base_unit`, `product_name` (mêmes champs que
 *     l'historique produit — pas de calcul parallèle).
 *   * Si `unit_saving_ht <= 0` OU pas de hausse → aucune génération.
 *   * Pas de gain annuel, pas de gain par commande, pas d'engagement.
 *   * Textarea VRAIMENT éditable ; « Réinitialiser » revient au brouillon.
 */
function SupplierNegotiationMessage({ dossier }) {
  const top = dossier?.top_hikes?.[0];
  // Feb 2026 §« brouillon personnalisé par fournisseur » — Le corps + le
  // sujet sont désormais construits côté backend (`negotiation_email.py`)
  // à partir de tout le dossier : hausses triées par impact €, contexte
  // d'achats (montant HT, période, nb factures), sélection dynamique
  // 1 / 2-3 références. En cas d'absence de `email_draft` (compat.
  // ancienne API), on retombe sur le brouillon mono-produit historique.
  const backendDraft = dossier?.email_draft || null;
  const defaultSubject = backendDraft?.subject || "";
  const defaultMessage = useMemo(() => {
    if (backendDraft?.body) return backendDraft.body;
    if (!top) return "";
    const u = top.base_unit === "u" ? "pièce" : top.base_unit;
    const before = Number(top.price_before_ht).toFixed(2).replace(".", ",");
    const now = Number(top.price_current_ht).toFixed(2).replace(".", ",");
    return (
`Bonjour,

Le tarif de ${top.product_name.toLowerCase()} est passé de ${before} à ${now} €/${u} entre nos deux dernières factures. Cette hausse réduit notre marge.

Pouvez-vous revoir ce tarif et vous rapprocher du précédent prix ?

Merci pour votre retour.`
    );
  }, [top, backendDraft]);

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultMessage);
  useEffect(() => { setSubject(defaultSubject); setBody(defaultMessage); }, [defaultSubject, defaultMessage]);

  if (!top && !backendDraft) return null;
  const copyAll = () => {
    const composed = subject ? `Objet : ${subject}\n\n${body}` : body;
    navigator.clipboard.writeText(composed);
    toast.success("Message copié.");
  };
  return (
    <>
      {defaultSubject && (
        <div className="mb-2">
          <Label className="text-[11px] uppercase tracking-widest font-bold text-[#5E6875]">Objet</Label>
          <Input
            data-testid="supplier-dossier-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1"
          />
        </div>
      )}
      <Textarea
        data-testid="supplier-dossier-argument"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={10}
        className="text-[13px] leading-relaxed"
      />
      <div className="flex flex-wrap gap-2 mt-2">
        <Button
          size="sm"
          variant="outline"
          className="gap-2"
          data-testid="supplier-dossier-copy"
          onClick={copyAll}
        >
          <Copy className="w-4 h-4" /> Copier
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="gap-2 text-[#5E6875]"
          data-testid="supplier-dossier-reset"
          onClick={() => { setSubject(defaultSubject); setBody(defaultMessage); }}
        >
          Réinitialiser
        </Button>
      </div>
      {backendDraft?.notes?.length > 0 && (
        <div className="mt-3 text-[11px] text-[#5E6875] italic space-y-1" data-testid="supplier-dossier-notes">
          {backendDraft.notes.map((n, i) => <div key={i}>· {n}</div>)}
        </div>
      )}
    </>
  );
}

/**
 * ProductDossierMessage — Feb 2026 §« brouillon bref éditable » pour le
 * dossier PRODUIT (`openDossier` → `product_dossier` backend). Reprend
 * produit/prix réels du dossier, format identique à l'exemple validé :
 *
 *   « Bonjour, le tarif de <produit> est passé de <avant> à <actuel>
 *     €/<unité> entre nos deux dernières factures. Cette hausse réduit
 *     notre marge. Pouvez-vous revoir ce tarif et vous rapprocher du
 *     précédent prix ? Merci pour votre retour. »
 *
 * Textarea VRAIMENT éditable. Boutons Copier / Réinitialiser.
 */
function ProductDossierMessage({ dossier }) {
  // Feb 2026 §« brouillon backend » — reprend `email_draft` fourni par
  // `product_dossier` (déterministe, adapté aux cas 0/1 hausse). En cas
  // d'absence, fallback local mono-référence identique à l'existant.
  const backendDraft = dossier?.email_draft || null;
  const defaultSubject = backendDraft?.subject || "";
  const defaultMessage = useMemo(() => {
    if (backendDraft?.body) return backendDraft.body;
    if (!dossier) return "";
    const gpe = dossier.gain_potentiel_estime || {};
    const u = gpe.unit_base === "u" ? "pièce" : (gpe.unit_base || "");
    const before = Number(gpe.target_price_ht ?? dossier.target_price_ht);
    const now = Number(gpe.current_price_ht ?? dossier.current_price_ht);
    const name = (dossier.product?.name || "ce produit").toLowerCase();
    if (!Number.isFinite(before) || !Number.isFinite(now) || !u) {
      return `Bonjour,\n\nLe tarif de ${name} a évolué entre nos deux dernières factures. Cette hausse réduit notre marge.\n\nPouvez-vous revoir ce tarif et vous rapprocher du précédent prix ?\n\nMerci pour votre retour.`;
    }
    const bStr = before.toFixed(2).replace(".", ",");
    const nStr = now.toFixed(2).replace(".", ",");
    return `Bonjour,\n\nLe tarif de ${name} est passé de ${bStr} à ${nStr} €/${u} entre nos deux dernières factures. Cette hausse réduit notre marge.\n\nPouvez-vous revoir ce tarif et vous rapprocher du précédent prix ?\n\nMerci pour votre retour.`;
  }, [dossier, backendDraft]);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultMessage);
  useEffect(() => { setSubject(defaultSubject); setBody(defaultMessage); }, [defaultSubject, defaultMessage]);
  const copyAll = () => {
    const composed = subject ? `Objet : ${subject}\n\n${body}` : body;
    navigator.clipboard.writeText(composed);
    toast.success("Message copié.");
  };
  return (
    <div>
      <h4 className="font-semibold text-[#0B1F33] mb-2 flex items-center gap-2">
        <Mail className="w-4 h-4" /> Brouillon d'email (modifiable avant envoi)
      </h4>
      {defaultSubject && (
        <div className="mb-2">
          <Label className="text-[11px] uppercase tracking-widest font-bold text-[#5E6875]">Objet</Label>
          <Input
            data-testid="dossier-email-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1"
          />
        </div>
      )}
      <Textarea
        data-testid="dossier-email-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={10}
        className="text-[13px] leading-relaxed"
      />
      <div className="flex flex-wrap gap-2 mt-2">
        <Button
          size="sm"
          variant="outline"
          className="gap-2"
          data-testid="dossier-copy-email"
          onClick={copyAll}
        >
          <Copy className="w-4 h-4" /> Copier
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="gap-2 text-[#5E6875]"
          data-testid="dossier-reset-email"
          onClick={() => { setSubject(defaultSubject); setBody(defaultMessage); }}
        >
          Réinitialiser
        </Button>
      </div>
      {backendDraft?.notes?.length > 0 && (
        <div className="mt-3 text-[11px] text-[#5E6875] italic space-y-1" data-testid="dossier-email-notes">
          {backendDraft.notes.map((n, i) => <div key={i}>· {n}</div>)}
        </div>
      )}
    </div>
  );
}
